import {
  Button, type ColumnDef, DataTable, FileUpload, formatBytes, InlineMessage, Spinner, Stack, Text, TextLink, type UploadItem,
} from '@olytic/foundation';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { recordPath } from '../objects/routes';
import { shortDate } from './model';
import {
  fetchProjectFiles, openFile, removeFile, uploadFile, type WorkBootstrap, type WorkFile, type WorkProject,
} from './workApi';

export const ATTACH_ACCEPT = '.pdf,.png,.jpg,.jpeg,.csv,.txt,.docx,.xlsx,.pptx';

/** A file as an attachment row; `where` adds the work item it is on. */
export function attachmentOf(file: WorkFile, boot: WorkBootstrap, where?: (file: WorkFile) => React.ReactNode): UploadItem {
  const by = boot.people.find((p) => p.id === file.owner_id)?.name;
  return {
    id: file.id,
    name: file.name,
    size: file.byte_size,
    state: 'ready',
    meta: <>{where ? where(file) : null}{by ? `${by} · ` : ''}{shortDate(file.uploaded_at)}</>,
  };
}

/** Every file on a project, with the record each one is attached to. */
export function WorkFiles({
  project, boot, onOpen, say,
}: {
  project: WorkProject;
  boot: WorkBootstrap;
  onOpen: (itemId: string) => void;
  say: (text: string, tone?: 'info' | 'error') => void;
}): JSX.Element {
  const [files, setFiles] = useState<WorkFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<UploadItem[]>([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    fetchProjectFiles(authenticatedFetch, project.id)
      .then((f) => live && setFiles(f))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the files.'));
    return () => {
      live = false;
    };
  }, [project.id, reload]);

  const add = async (list: File[]) => {
    setUploading(list.map((f, i) => ({ id: `up-${i}-${f.name}`, name: f.name, size: f.size, state: 'uploading' })));
    let done = 0;
    for (const f of list) {
      try {
        await uploadFile(authenticatedFetch, 'project', project.id, f);
        done += 1;
      } catch (e) {
        say(e instanceof Error ? e.message : `Could not attach ${f.name}.`, 'error');
      }
    }
    setUploading([]);
    if (done) say(`Attached ${done} file${done === 1 ? '' : 's'} to ${project.code}`);
    setReload((n) => n + 1);
  };

  if (error) return <InlineMessage tone="error" role="alert">{error}</InlineMessage>;
  if (!files) return <div className="flex justify-center py-10"><Spinner label="Loading files" /></div>;

  const open = (file: WorkFile) =>
    openFile(authenticatedFetch, file).catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not open the file.', 'error'));
  const remove = (file: WorkFile) =>
    removeFile(authenticatedFetch, file.id, file.entity_type, file.entity_id)
      .then(() => {
        say(`Removed ${file.name}`);
        setReload((n) => n + 1);
      })
      .catch((e: unknown) => say(e instanceof Error ? e.message : 'Could not remove the file.', 'error'));

  const columns: ColumnDef<WorkFile>[] = [
    {
      key: 'name',
      label: 'Name',
      width: '34%',
      sortable: true,
      get: (f) => f.name,
      render: (f) => (
        <span className="flex min-w-0 items-center gap-2">
          <Button variant="link" className="min-w-0 justify-start whitespace-normal break-words text-left" title={f.name} onClick={() => void open(f)}>{f.name}</Button>
          {f.stored ? null : <span className="shrink-0 text-xs text-[var(--fg-muted)]">Not stored</span>}
        </span>
      ),
    },
    {
      key: 'linked',
      label: 'Linked to',
      width: '30%',
      sortable: true,
      get: (f) => (f.entity_type === 'work_item' ? f.item_key ?? '' : ''),
      render: (f) => (f.entity_type === 'work_item' ? (
        <span className="flex min-w-0 items-center gap-2">
          <Button variant="link" className="shrink-0 text-xs" onClick={() => onOpen(f.entity_id)}>{f.item_key}</Button>
          <span className="truncate text-[var(--fg-strong)]" title={f.item_title ?? ''}>{f.item_title}</span>
        </span>
      ) : (
        <TextLink asChild>
          <Link to={recordPath('projects', f.entity_id)}>
            <span className="text-xs">{project.code}</span> {project.name}
          </Link>
        </TextLink>
      )),
    },
    { key: 'owner', label: 'Uploaded by', sortable: true, get: (f) => boot.people.find((p) => p.id === f.owner_id)?.name ?? '' },
    { key: 'uploaded_at', label: 'Uploaded', sortable: true, get: (f) => f.uploaded_at, render: (f) => shortDate(f.uploaded_at), nowrap: true },
    { key: 'byte_size', label: 'Size', sortable: true, align: 'right', get: (f) => f.byte_size, render: (f) => formatBytes(f.byte_size), nowrap: true },
  ];

  return (
    <Stack gap="md" className="pt-4">
      <Text as="h3" className="m-0 font-emphasis text-[var(--fg-strong)]">Files</Text>
      <DataTable<WorkFile>
        columns={columns}
        rows={files}
        getRowId={(f) => `${f.id}:${f.entity_type}:${f.entity_id}`}
        rowActions={[{ label: 'Remove', destructive: true, onSelect: (f) => void remove(f) }]}
        rowActionsTrigger="chevron"
        empty={<Text tone="muted" size="sm">No files on {project.name} yet.</Text>}
      />
      <FileUpload
        variant="attachments"
        accept={ATTACH_ACCEPT}
        maxSizeMb={15}
        attachLabel="Attach files to the project"
        attachHint="or drop them here. To attach to a work item, open it."
        items={uploading}
        onAdd={(list) => void add(list)}
      />
    </Stack>
  );
}
